import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, forkJoin, map, of } from 'rxjs';
import { CountryDto, LocalityDto, StreetDto } from '@shared/interfaces/dtos/addresses';
import { Locality, NewPostalCodeAndStreet, Street } from '@shared/interfaces/addresses';
import { environment } from 'src/environments/environment';
import { NewContactAddress, UpdateContactAddress } from '@shared/interfaces/contacts';

@Injectable({
  providedIn: 'root',
})
export class AddressesService {
  private readonly http = inject(HttpClient);
  private readonly iAdminBaseUrl = `${environment.apiIContactBaseUrlAdmin}/api/v1`;
  private readonly contactsUrl = `${environment.apiIContactBaseUrl}/v1`;

  public fetchCountries(): Observable<CountryDto[]> {
    return this.http.get<CountryDto[]>(`${this.iAdminBaseUrl}/Global/pays`);
  }

  public fetchCountryLocalities(countryId: number): Observable<Locality[]> {
    return this.http
      .get<LocalityDto[]>(`${this.contactsUrl}/Global/pays/${countryId}/CodesPostaux`)
      .pipe(map((res) => res.map((dto) => this.toLocality(dto))));
  }

  public fetchLocalityStreets(countryId: number, postalCode: number): Observable<Street[]> {
    return this.http
      .get<StreetDto[]>(`${this.contactsUrl}/Global/pays/${countryId}/CodesPostaux/${postalCode}/rues`)
      .pipe(map((res) => res.map((dto) => this.toStreet(dto))));
  }

  public insertNewPostalCodeAndStreet(
    dto: NewPostalCodeAndStreet
  ): Observable<{ postalCodeId: number; streetId: number }> {
    return this.http
      .post<{ codePostal: { id: number }; rue: { id: number } }>(`${this.contactsUrl}/Management/CodesPostauxRues`, dto)
      .pipe(map((res) => ({ postalCodeId: res.codePostal.id, streetId: res.rue.id })));
  }

  public insertOneAddress(contactId: number, dto: NewContactAddress): Observable<unknown> {
    return this.http.post<unknown>(`${this.contactsUrl}/contacts/${contactId}/contactAdresses`, dto);
  }

  public updateOneAddressOrInsertItIfDoesntExist(config: {
    contactId: number;
    addressId: number | null;
    dto: UpdateContactAddress;
  }): Observable<unknown> {
    return config.addressId
      ? this.http.put<unknown>(
          `${this.contactsUrl}/contacts/${config.contactId}/contactAdresses/${config.addressId}`,
          config.dto
        )
      : this.http.post<unknown>(`${this.contactsUrl}/contacts/${config.contactId}/contactAdresses`, config.dto);
  }

  public updateContactAddresses(config: {
    contactId: number;
    addresses: {
      id: number | null;
      dto: UpdateContactAddress;
    }[];
  }): Observable<unknown> {
    const reqs: Observable<unknown>[] = [];
    const { contactId, addresses } = config;

    if (!addresses.length) return of([]);

    for (const address of addresses) {
      const { id, dto } = address;

      reqs.push(this.updateOneAddressOrInsertItIfDoesntExist({ contactId, dto, addressId: id }));
    }

    return forkJoin(reqs);
  }

  public deleteOneAddress(contactId: number, addressId: number): Observable<unknown> {
    return this.http.delete<unknown>(`${this.contactsUrl}/contacts/${contactId}/contactAdresses/${addressId}`);
  }

  private toLocality(dto: LocalityDto): Locality {
    return {
      id: dto.id,
      name: dto.localite ?? 'Localité inconnue',
      postalCode: dto.codePostal,
    };
  }

  private toStreet(dto: StreetDto): Street {
    return {
      id: dto.id,
      name: dto.rueNom ?? 'Rue inconnue',
      postalCode: dto.codePostal,
      streetCode: dto.codeRue,
    };
  }
}
